/**
 * <cart-drawer-v2> — custom cart drawer for Agent84.
 *
 * Markup: snippets/cart-drawer-v2.liquid, settings: sections/cart-drawer-v2.liquid.
 * Only loaded when Theme settings > Cart > "Cart drawer style" is "Custom drawer";
 * the stock Impulse drawer (theme.CartDrawer) is not constructed in that mode
 * because layout/theme.liquid reports a cartType other than 'drawer'.
 *
 * Add-to-cart is NOT intercepted here. Every add path in this theme
 * (theme.AjaxProduct, quick-add.js, look-set-drawer.js) already posts to
 * /cart/add.js itself and announces `ajaxProduct:added` on the document, so this
 * class listens for that instead. Intercepting the form submit as well would
 * double-add: theme.AjaxProduct binds its handler on the form and only calls
 * preventDefault(), which does not stop the event bubbling to the document.
 */
class CartDrawerV2 extends HTMLElement {
  constructor() {
    super();
    // Mirrors the merchant's free-shipping threshold (in cents) as rendered into
    // data-threshold by snippets/cart-drawer-v2.liquid. All progress/threshold
    // math is done Liquid-side and stays there; this field exists purely so JS
    // consumers can read the same value the current markup was rendered with.
    this.threshold = parseInt(this.dataset.threshold, 10) || 0;
    this.isUnlockedState = false;
    this.confettiAnimId = null;
    this.productCache = {};
    this.isDetailView = false;
    this.currentCart = null;
  }

  connectedCallback() {
    this.panel = this.querySelector('.cartv2__panel');
    this.overlay = this.querySelector('.cartv2__overlay');
    this.canvas = document.getElementById('cartv2-confetti-canvas');

    this.bindEvents();
    this.checkInitialState();
  }

  t(key, fallback = '') {
    const config = window.cart_drawer_v2;
    return (config && config.translations && config.translations[key]) || fallback;
  }

  /**
   * Delegates to Impulse's own money formatter so the drawer honours the shop's
   * money_format (and the currency-code variant) rather than assuming USD. Falls
   * back to a plain dollar string only if theme.js has not parsed yet.
   */
  formatMoney(cents) {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '';

    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(amount, theme.settings && theme.settings.moneyFormat);
    }
    return '$' + (amount / 100).toFixed(2);
  }

  /**
   * Merchandise units only — the shipping-insurance line is a real cart line and
   * must never show up in the header count. Falls back to the raw Shopify count
   * when assets/shipping-insurance.js is absent or insurance is switched off.
   */
  merchandiseCount(cart) {
    const filters = window.CartDrawerFilters;
    if (filters && typeof filters.merchandiseCount === 'function') {
      return filters.merchandiseCount(cart);
    }
    return cart ? cart.item_count : 0;
  }

  /**
   * Announces every cart render on the document. `source` lets a listener that
   * caused the mutation recognise and skip its own event instead of reacting to it
   * and looping.
   */
  dispatchCartUpdated(cart, source) {
    document.dispatchEvent(
      new CustomEvent('cart-drawer:updated', {
        bubbles: true,
        detail: { cart: cart, source: source || 'cart-drawer-v2' }
      })
    );
  }

  /**
   * External cart mutation (shipping insurance). Re-renders from the section
   * endpoint, then re-announces with the caller's source so the caller can ignore
   * its own event instead of looping.
   */
  async renderFromExternal(cart, source) {
    this.currentCart = cart;
    if (!this.isDetailView) {
      await this.renderCartState(cart);
    }
    this.updateHeaderBadge(this.merchandiseCount(cart));
    this.dispatchCartUpdated(cart, source);
  }

  /* ----------------------------------------------------------------- confetti */

  /**
   * Confetti palette: shades of the theme's button colour rather than a fixed
   * rainbow, so it reads as the brand celebrating rather than generic party
   * graphics. Read from --colorBtnPrimary (emitted by theme-settings-tokens) so
   * it tracks the merchant's button setting with no second place to update.
   *
   * The ramp is the base colour plus two tints toward white and two shades toward
   * black. Pure white and pure black are avoided — white is invisible on the
   * drawer's white card and black reads as dirt.
   */
  confettiColors() {
    const fallback = ['#F12479', '#F55694', '#F888B1', '#C11D61', '#911648'];

    const base = this.parseColor(
      getComputedStyle(document.documentElement).getPropertyValue('--colorBtnPrimary')
    );
    if (!base) return fallback;

    const mix = (channel, target, amount) => Math.round(channel + (target - channel) * amount);
    const toHex = (rgb) => '#' + rgb.map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('');

    return [
      toHex(base),
      toHex(base.map((c) => mix(c, 255, 0.25))),
      toHex(base.map((c) => mix(c, 255, 0.5))),
      toHex(base.map((c) => mix(c, 0, 0.2))),
      toHex(base.map((c) => mix(c, 0, 0.4)))
    ];
  }

  /** `#rgb`, `#rrggbb` or `rgb()/rgba()` to [r,g,b]. Null when unrecognised. */
  parseColor(value) {
    const input = String(value || '').trim();
    if (!input) return null;

    const hex = input.replace('#', '');
    if (/^[0-9a-f]{3}$/i.test(hex)) {
      return [0, 1, 2].map((i) => parseInt(hex[i] + hex[i], 16));
    }
    if (/^[0-9a-f]{6}$/i.test(hex)) {
      return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
    }

    const parts = input.match(/rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i);
    if (parts) return [1, 2, 3].map((i) => Math.round(parseFloat(parts[i])));

    return null;
  }

  triggerFallingConfetti() {
    // Merchant setting. Only an explicit `false` disables it, so a missing flag
    // (older markup, section not yet re-rendered) keeps the default behaviour.
    if (window.cart_drawer_v2 && window.cart_drawer_v2.confetti_enabled === false) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const canvas = this.canvas || document.getElementById('cartv2-confetti-canvas');
    if (!canvas) return;

    const cardEl = this.querySelector('.cartv2__card') || canvas.parentElement;
    const width = cardEl ? cardEl.clientWidth : 420;
    const height = cardEl ? cardEl.clientHeight : window.innerHeight;

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    const particleCount = 100;
    const particles = [];
    const colors = this.confettiColors();

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * width,
        y: -15 - Math.random() * 120, // Start above top of the card
        width: 6 + Math.random() * 7,
        height: 10 + Math.random() * 8,
        color: colors[Math.floor(Math.random() * colors.length)],
        vx: (Math.random() - 0.5) * 2.2,
        vy: 2.8 + Math.random() * 4,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
        oscillationSpeed: 0.03 + Math.random() * 0.05,
        oscillationAmplitude: 1.2 + Math.random() * 1.8
      });
    }

    if (this.confettiAnimId) {
      cancelAnimationFrame(this.confettiAnimId);
    }

    let frameCount = 0;
    const maxFrames = 240; // ~4s duration at 60fps

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frameCount++;
      let activeParticles = 0;

      particles.forEach((p) => {
        if (p.y < canvas.height + 30) {
          activeParticles++;
          p.y += p.vy;
          p.x += p.vx + Math.sin(frameCount * p.oscillationSpeed) * p.oscillationAmplitude;
          p.rotation += p.rotationSpeed;

          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation);
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.width / 2, -p.height / 2, p.width, p.height);
          ctx.restore();
        }
      });

      if (activeParticles > 0 && frameCount < maxFrames) {
        this.confettiAnimId = requestAnimationFrame(animate);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };

    animate();
  }

  /* ------------------------------------------------------------------- events */

  bindEvents() {
    // Intercept clicks on cart triggers and links targeting /cart across the site.
    document.addEventListener('click', (e) => {
      const cartLink =
        e.target.closest('a[href$="/cart"]') ||
        e.target.closest('.js-drawer-open-cart') ||
        e.target.closest('[data-cart-drawer-toggle]');

      if (cartLink) {
        const href = cartLink.getAttribute('href') || '';
        // Never swallow a checkout link, and let a modified click (new tab,
        // download, middle click) reach the browser.
        const isModified = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
        if (!href.includes('/checkout') && !isModified) {
          e.preventDefault();
          this.openCart();
          return;
        }
      }

      // Close cart button / overlay / empty-state continue button
      if (e.target.closest('[data-cart-close]')) {
        e.preventDefault();
        this.closeCart();
        return;
      }

      // Back button click in detail view
      if (e.target.closest('[data-cart-back]')) {
        e.preventDefault();
        this.hideProductDetails();
        return;
      }

      // Show Details button click
      const showDetailsBtn = e.target.closest('[data-fbt-show-details]');
      if (showDetailsBtn) {
        e.preventDefault();
        const handle = showDetailsBtn.dataset.handle;
        if (handle) {
          this.showProductDetails(handle);
        }
        return;
      }

      // Upsell / detail-view Add to Cart button click
      const fbtAtcBtn = e.target.closest('[data-fbt-atc]');
      if (fbtAtcBtn) {
        e.preventDefault();
        const handle = fbtAtcBtn.dataset.handle;
        const variantId = fbtAtcBtn.dataset.variantId;
        const hasVariants = fbtAtcBtn.dataset.hasVariants === 'true';

        if (handle && hasVariants) {
          this.openVariantModal(handle);
        } else if (variantId) {
          this.addVariant(variantId, fbtAtcBtn);
        } else if (handle) {
          this.openVariantModal(handle);
        }
        return;
      }

      // Variant modal close button
      if (e.target.closest('[data-variant-modal-close]')) {
        e.preventDefault();
        this.closeVariantModal();
        return;
      }

      // Variant confirm button inside modal
      const confirmBtn = e.target.closest('#cartv2-variant-confirm');
      if (confirmBtn) {
        e.preventDefault();
        const select = this.querySelector('#cartv2-variant-select');
        if (select && select.value) {
          this.addVariant(select.value, confirmBtn);
          this.closeVariantModal();
        }
        return;
      }

      // Quantity controls (+ / -)
      const qtyBtn = e.target.closest('[data-qty-change]');
      if (qtyBtn) {
        e.preventDefault();
        const lineKey = qtyBtn.dataset.lineKey;
        const currentQty = parseInt(qtyBtn.dataset.currentQty, 10);
        const change = parseInt(qtyBtn.dataset.qtyChange, 10);
        const newQty = Math.max(0, currentQty + change);
        this.updateLineItem(lineKey, newQty);
        return;
      }

      // Remove item button
      const removeBtn = e.target.closest('[data-remove-item]');
      if (removeBtn) {
        e.preventDefault();
        this.updateLineItem(removeBtn.dataset.lineKey, 0);
        return;
      }

      // Discount code apply. Delegated, because the drawer replaces the whole
      // body on every cart mutation and a bound listener would be orphaned.
      const discountBtn = e.target.closest('[data-discount-apply]');
      if (discountBtn) {
        e.preventDefault();
        this.applyDiscountCode(discountBtn);
      }
    });

    // Enter inside the discount field submits it.
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.closest('[data-discount-input]')) {
        e.preventDefault();
        const applyBtn = this.querySelector('[data-discount-apply]');
        if (applyBtn) this.applyDiscountCode(applyBtn);
        return;
      }

      if (e.key === 'Escape' && this.classList.contains('is-open')) {
        const modal = this.querySelector('#cartv2-variant-modal');
        if (modal && modal.classList.contains('is-open')) {
          this.closeVariantModal();
        } else {
          this.closeCart();
        }
      }
    });

    // Every add-to-cart path in this theme announces this once the line has
    // landed, so the drawer refreshes and opens off the back of it.
    //
    // `detail.product` is the success test, not decoration. theme.AjaxProduct
    // fires this event on its 422 branch too — sold out, unavailable, over the
    // stock ceiling — with no detail at all, purely so the stock drawer rebuilds.
    // Acting on that slid the drawer open over the inline error the product form
    // had just rendered, telling the shopper the add worked when it did not. Every
    // path that really added a line (theme.AjaxProduct.success, quick-add.js,
    // look-set-drawer.js) carries detail.product.
    //
    // Open without waiting on the refresh. refreshCart() costs two sequential
    // round-trips (/cart.js, then the section render), and opening afterwards
    // meant nothing happened on screen for the whole of both — the shopper
    // clicked Add to cart and watched a still page. The drawer slides in on the
    // event with its spinner up and fills in as the responses land.
    //
    // The add-on popup (assets/upsell-popup.js) listens for this same event and
    // claims the add synchronously via window.theme.cartOpenGate while it works
    // out whether it has anything to offer. Deferring the open by a microtask
    // runs after every synchronous listener for this dispatch — so the gate is
    // already held by then — and costs nothing perceptible when nothing claims.
    // Without it the drawer painted first and the popup arrived on top of it,
    // leaving the shopper with cart, then add-on, then cart again.
    //
    // The refresh is NOT deferred with it: whether or not we open, the cart data
    // and the header badge have to catch up, so the drawer is already correct
    // when the popup's own close reveals it (cart:open only opens — it does not
    // refresh).
    document.addEventListener('ajaxProduct:added', async (event) => {
      if (!event.detail || !event.detail.product) return;

      this.isDetailView = false;

      const refresh = this.refreshCart();

      await Promise.resolve();
      const gate = window.theme && window.theme.cartOpenGate;
      if (!gate || !gate.isHeld()) {
        this.openCart();
      }

      await refresh;
    });

    // Impulse's dev-facing cart events, kept working for other theme code.
    document.addEventListener('cart:open', () => this.openCart());
    document.addEventListener('cart:close', () => this.closeCart());
    document.addEventListener('cart:build', () => this.refreshCart());

    // Keep the confetti canvas matched to the card.
    window.addEventListener('resize', () => {
      const canvas = this.canvas || document.getElementById('cartv2-confetti-canvas');
      if (!canvas) return;
      const cardEl = this.querySelector('.cartv2__card') || canvas.parentElement;
      canvas.width = cardEl ? cardEl.clientWidth : 420;
      canvas.height = cardEl ? cardEl.clientHeight : window.innerHeight;
    });
  }

  checkInitialState() {
    const msgEl = this.querySelector('#cartv2-free-shipping-msg');
    if (msgEl) {
      this.isUnlockedState = msgEl.dataset.unlocked === 'true';
    }
  }

  /* --------------------------------------------------------------- open/close */

  showLoading() {
    const overlay = this.querySelector('.cartv2__loading-overlay');
    if (overlay) overlay.classList.add('is-loading');
  }

  hideLoading() {
    const overlay = this.querySelector('.cartv2__loading-overlay');
    if (overlay) overlay.classList.remove('is-loading');
  }

  openCart() {
    this.classList.add('is-open');
    if (this.panel) this.panel.classList.add('is-open');
    if (this.overlay) this.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('cart-drawer-open');

    // Impulse ships a focus trap; use it when present so the drawer behaves like
    // every other drawer in the theme.
    if (this.panel && window.theme && theme.a11y && typeof theme.a11y.trapFocus === 'function') {
      theme.a11y.trapFocus({
        container: this.panel,
        elementToFocus: this.panel,
        namespace: 'cartdrawerv2_focus'
      });
    }

    if (this.isUnlockedState) {
      this.triggerFallingConfetti();
    }
  }

  closeCart() {
    this.classList.remove('is-open');
    if (this.panel) this.panel.classList.remove('is-open');
    if (this.overlay) this.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    document.body.classList.remove('cart-drawer-open');

    if (this.panel && window.theme && theme.a11y && typeof theme.a11y.removeTrapFocus === 'function') {
      theme.a11y.removeTrapFocus({
        container: this.panel,
        namespace: 'cartdrawerv2_focus'
      });
    }

    this.hideProductDetails();
    this.closeVariantModal();
  }

  /* ------------------------------------------------------------ cart mutation */

  async refreshCart() {
    this.showLoading();
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.currentCart = cart;

      if (!this.isDetailView) {
        await this.renderCartState(cart);
      }
      this.updateHeaderBadge(this.merchandiseCount(cart));
      this.dispatchCartUpdated(cart, 'cart-drawer-v2');
    } catch (err) {
      console.error('Error refreshing cart:', err);
    } finally {
      this.hideLoading();
    }
  }

  async updateLineItem(lineKey, quantity) {
    this.showLoading();
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: quantity })
      });
      const cart = await res.json();
      this.currentCart = cart;
      if (!this.isDetailView) {
        await this.renderCartState(cart);
      }
      this.updateHeaderBadge(this.merchandiseCount(cart));
      this.dispatchCartUpdated(cart, 'cart-drawer-v2');
    } catch (err) {
      console.error('Error updating cart line item:', err);
    } finally {
      this.hideLoading();
    }
  }

  async addVariant(variantId, buttonEl) {
    const label = buttonEl ? buttonEl.querySelector('span') : null;

    if (buttonEl) {
      buttonEl.disabled = true;
      buttonEl.classList.add('kit-button--loading');
      if (label) label.innerText = this.t('adding', 'Adding...');
    }

    // Same ordering as the ajaxProduct:added handler: on screen first, network
    // after. Every caller is already inside an open drawer, so this only really
    // matters for leaving the in-drawer product view — but it keeps the two add
    // paths reading the same way.
    this.isDetailView = false;
    this.openCart();
    this.showLoading();
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      // /cart/add.js answers 4xx with a JSON body rather than rejecting, so a
      // sold-out or unavailable variant has to be caught here — otherwise
      // refreshCart() below quietly re-renders the unchanged cart as if the add
      // had worked, and the shopper is told nothing.
      const added = await res.json();
      if (added && added.status && Number(added.status) >= 400) {
        throw new Error(added.description || added.message || 'Add to cart failed');
      }
      await this.refreshCart();
    } catch (err) {
      console.error('Error adding variant to cart:', err);
    } finally {
      this.hideLoading();
      if (buttonEl) {
        buttonEl.disabled = false;
        buttonEl.classList.remove('kit-button--loading');
        if (label) label.innerText = this.t('add_to_cart', 'Add to cart');
      }
    }
  }

  /* ----------------------------------------------------------- discount code */

  /**
   * Shopify honours `return_to` only for a code it accepts; an invalid code
   * redirects to /cart without it, which is what the marker query param detects.
   * A code can be valid yet not reduce this cart (wrong collection, minimum not
   * met), so the cart is re-read and checked for a matching allocation.
   */
  async applyDiscountCode(applyBtn) {
    const wrapper = applyBtn.closest('.cartv2__discount') || this;
    const codeInput = wrapper.querySelector('[data-discount-input]');
    const msgEl = wrapper.querySelector('[data-discount-msg]');
    if (!codeInput) return;

    const code = codeInput.value.trim();
    if (!code) return;

    const showMsg = (text, type) => {
      if (!msgEl) return;
      msgEl.textContent = text;
      msgEl.className = 'cartv2__discount-msg cartv2__discount-msg--' + type;
      msgEl.hidden = false;
    };

    const originalLabel = applyBtn.textContent;
    applyBtn.disabled = true;
    applyBtn.classList.add('kit-button--loading');
    applyBtn.textContent = this.t('applying', 'Applying...');
    if (msgEl) {
      msgEl.hidden = true;
      msgEl.className = 'cartv2__discount-msg';
    }

    try {
      const returnTo = encodeURIComponent('/cart?__dcheck=1');
      const res = await fetch('/discount/' + encodeURIComponent(code) + '?return_to=' + returnTo, {
        credentials: 'same-origin',
        redirect: 'follow'
      });

      if (!res.url || !res.url.includes('__dcheck=1')) {
        showMsg(this.t('discount_invalid', 'This discount code is invalid or has expired.'), 'error');
        return;
      }

      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
      const codeApplied = (cart.cart_level_discount_applications || []).some(
        (d) =>
          d.type === 'discount_code' &&
          d.title &&
          d.title.toLowerCase() === code.toLowerCase() &&
          d.total_allocated_amount > 0
      );

      if (codeApplied) {
        showMsg(this.t('discount_applied', 'Discount applied!'), 'success');
        await this.refreshCart();
      } else {
        showMsg(
          this.t('discount_not_applicable', 'This discount code cannot be applied to your cart.'),
          'error'
        );
      }
    } catch (err) {
      showMsg(this.t('discount_error', 'Something went wrong. Please try again.'), 'error');
    } finally {
      applyBtn.disabled = false;
      applyBtn.classList.remove('kit-button--loading');
      applyBtn.textContent = this.t('apply', originalLabel);
    }
  }

  /* ------------------------------------------------------------ header badge */

  /**
   * Impulse's header shows a dot rather than a number, so the count drives the
   * dot's visibility. `[data-cart-count]` (the drawer's own title counter, and
   * any theme element opting in) still receives the figure.
   */
  updateHeaderBadge(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = '(' + count + ')';
    });

    document.querySelectorAll('.cart-link__bubble').forEach((bubble) => {
      bubble.classList.toggle('cart-link__bubble--visible', count > 0);
    });
  }

  /* --------------------------------------------------------- product details */

  async fetchProductData(handle) {
    if (this.productCache[handle]) return this.productCache[handle];

    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      const data = await res.json();
      this.productCache[handle] = data;
      return data;
    } catch (err) {
      console.error('Error fetching product details:', err);
      return null;
    }
  }

  async showProductDetails(handle) {
    this.showLoading();
    try {
      const product = await this.fetchProductData(handle);
      if (!product) return;

      this.isDetailView = true;

      const backBtn = this.querySelector('[data-cart-back]');
      if (backBtn) backBtn.classList.remove('is-hidden');

      const bodyEl = this.querySelector('.cartv2__body');
      if (!bodyEl) return;

      const imageSrc = product.featured_image || (product.images && product.images[0]) || '';
      const hasVariants = product.variants && product.variants.length > 1;
      const firstVariantId = product.variants && product.variants[0] ? product.variants[0].id : '';

      // product.description is store-authored HTML from Shopify's own product
      // API, so it is inserted as markup on purpose. product.title is escaped
      // because it also lands in an alt attribute.
      bodyEl.innerHTML = `
        <div class="cartv2__detail-view">
          <img src="${imageSrc}" alt="${this.escapeHtml(product.title)}" class="cartv2__detail-img">
          <h3 class="cartv2__detail-title">${this.escapeHtml(product.title)}</h3>
          <p class="cartv2__detail-price">${this.formatMoney(product.price)}</p>
          <button type="button"
            class="kit-button kit-button--primary kit-button--md kit-button--full-width cartv2__detail-atc"
            data-fbt-atc
            data-handle="${this.escapeHtml(product.handle)}"
            data-variant-id="${firstVariantId}"
            data-has-variants="${hasVariants}">
            <span>${this.escapeHtml(this.t('add_to_cart', 'Add to cart'))}</span>
          </button>
          <div class="cartv2__detail-desc rte">${product.description || ''}</div>
        </div>
      `;
    } catch (err) {
      console.error('Error showing product details:', err);
    } finally {
      this.hideLoading();
    }
  }

  async hideProductDetails() {
    this.isDetailView = false;
    const backBtn = this.querySelector('[data-cart-back]');
    if (backBtn) backBtn.classList.add('is-hidden');

    await this.refreshCart();
  }

  escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (char) => {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
    });
  }

  /* ----------------------------------------------------- variant picker modal */

  async openVariantModal(handle) {
    this.showLoading();
    try {
      const product = await this.fetchProductData(handle);
      if (!product) return;

      const availableVariants = (product.variants || []).filter((v) => v.available);
      if (availableVariants.length === 0) return;

      // Only one choice left — no point asking.
      if (availableVariants.length === 1) {
        this.addVariant(availableVariants[0].id);
        return;
      }

      const modalEl = this.querySelector('#cartv2-variant-modal');
      const contentEl = this.querySelector('#cartv2-variant-modal-content');
      if (!modalEl || !contentEl) return;

      const imageSrc = product.featured_image || (product.images && product.images[0]) || '';
      const optionLabel = product.options && product.options[0] ? product.options[0].name : 'Option';
      const optionsHtml = availableVariants
        .map(
          (v) =>
            `<option value="${v.id}">${this.escapeHtml(v.title)} - ${this.formatMoney(v.price)}</option>`
        )
        .join('');

      contentEl.innerHTML = `
        <button type="button" class="cartv2__variant-modal-close" data-variant-modal-close aria-label="${this.escapeHtml(
          this.t('close', 'Close')
        )}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12"/>
          </svg>
        </button>
        <div class="cartv2__variant-modal-row">
          <div class="cartv2__variant-modal-img" style="--item-img-url: url('${imageSrc}');"></div>
          <div class="cartv2__variant-modal-info">
            <span class="cartv2__variant-modal-title">${this.escapeHtml(product.title)}</span>
            <span class="cartv2__variant-modal-price">${this.formatMoney(product.price)}</span>
          </div>
        </div>
        <label class="cartv2__variant-label" for="cartv2-variant-select">${this.escapeHtml(
          optionLabel
        )}</label>
        <select class="cartv2__variant-select" id="cartv2-variant-select">${optionsHtml}</select>
        <button type="button"
          class="kit-button kit-button--primary kit-button--md kit-button--full-width cartv2__variant-confirm-btn"
          id="cartv2-variant-confirm">
          <span>${this.escapeHtml(this.t('confirm', 'Confirm'))}</span>
        </button>
      `;
      modalEl.classList.add('is-open');
      modalEl.setAttribute('aria-hidden', 'false');
    } catch (err) {
      console.error('Error opening variant modal:', err);
    } finally {
      this.hideLoading();
    }
  }

  closeVariantModal() {
    const modalEl = this.querySelector('#cartv2-variant-modal');
    if (modalEl) {
      modalEl.classList.remove('is-open');
      modalEl.setAttribute('aria-hidden', 'true');
    }
  }

  /* ----------------------------------------------------------------- render */

  async fetchCartSection() {
    try {
      const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const res = await fetch(rootPath + '?sections=cart-drawer-v2');
      if (!res.ok) return null;
      const data = await res.json();
      return data['cart-drawer-v2'] || null;
    } catch (err) {
      console.error('Error fetching cart-drawer-v2 section:', err);
      return null;
    }
  }

  renderCartSectionHtml(sectionHtml) {
    if (!sectionHtml) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHtml, 'text/html');
    const newDrawer = doc.querySelector('cart-drawer-v2');
    if (!newDrawer) return;

    // Reset the back button if we are no longer in detail view.
    const backBtn = this.querySelector('[data-cart-back]');
    if (backBtn && !this.isDetailView) {
      backBtn.classList.add('is-hidden');
    }

    // Swap body and footer from the server-rendered section.
    const newBody = newDrawer.querySelector('.cartv2__body');
    const newFooter = newDrawer.querySelector('#cartv2-footer');

    const currentBody = this.querySelector('.cartv2__body');
    const currentFooter = this.querySelector('#cartv2-footer');

    if (newBody && currentBody) {
      currentBody.innerHTML = newBody.innerHTML;
    }

    if (newFooter && currentFooter) {
      currentFooter.innerHTML = newFooter.innerHTML;
      currentFooter.style.display = '';
    } else if (newFooter && !currentFooter) {
      // Cart went from empty to non-empty: the footer did not exist before.
      const card = this.querySelector('.cartv2__card');
      if (card) {
        const footerDiv = document.createElement('div');
        footerDiv.className = 'cartv2__footer';
        footerDiv.id = 'cartv2-footer';
        footerDiv.innerHTML = newFooter.innerHTML;
        card.appendChild(footerDiv);
      }
    } else if (!newFooter && currentFooter) {
      currentFooter.style.display = 'none';
    }

    // Confetti on the transition into the unlocked state, never on every render.
    const msgEl = this.querySelector('#cartv2-free-shipping-msg');
    if (msgEl) {
      const isNowUnlocked = msgEl.dataset.unlocked === 'true';
      if (isNowUnlocked && !this.isUnlockedState) {
        this.triggerFallingConfetti();
      }
      this.isUnlockedState = isNowUnlocked;
    }
  }

  async renderCartState() {
    const sectionHtml = await this.fetchCartSection();
    if (sectionHtml) {
      this.renderCartSectionHtml(sectionHtml);
    }
  }
}

if (!customElements.get('cart-drawer-v2')) {
  customElements.define('cart-drawer-v2', CartDrawerV2);
}

/**
 * Cart page handlers, bound at document level and independent of the custom
 * element above.
 *
 * These two behaviours belong to snippets/cart-item.liquid, which the /cart
 * template renders — not to the drawer. In stock mode they are provided by the
 * inline script in snippets/cart-drawer.liquid, which renders on every page
 * including /cart. In custom mode that snippet never renders, and the drawer
 * itself is deliberately absent on /cart (see sections/header.liquid), so
 * CartDrawerV2.bindEvents never runs there. Without this block the cart page's
 * Remove buttons and quantity ceilings would simply stop working.
 */
(function () {
  'use strict';

  /**
   * Remove a line from the cart page. Routed through Impulse's own
   * `cart:quantity<namespace>` event rather than a raw fetch, so theme.CartForm
   * does the full update it already knows how to do — line items, subtotal,
   * discounts, savings — and finishes by dispatching `cart:updated`, which is
   * what assets/shipping-insurance.js listens for to re-tier the fee.
   *
   * The namespace is derived from the enclosing form's data-location (the cart
   * page is `page`) rather than hardcoded: the stock snippet always dispatched
   * `.cart-cart-drawer`, which on /cart addressed the drawer's CartForm instead
   * of the page's own, so the row you removed never disappeared from the table.
   */
  document.addEventListener('click', function (event) {
    const btn = event.target.closest('[data-remove-key]');
    if (!btn) return;

    event.preventDefault();

    const form = btn.closest('form[data-location]');
    const namespace = '.cart-' + ((form && form.dataset.location) || 'page');

    document.dispatchEvent(
      new CustomEvent('cart:quantity' + namespace, { detail: [btn.dataset.removeKey, 0, null] })
    );
  });

  /**
   * Quantity ceiling for variants that deny overselling. snippets/cart-item.liquid
   * puts the limit on the wrapper as data-qty-max; capture phase so this runs
   * before theme.js's own plus-button handler and can stop it.
   */
  document.addEventListener(
    'click',
    function (event) {
      const plusBtn = event.target.closest('.js-qty__adjust--plus');
      if (!plusBtn) return;

      const wrapper = plusBtn.closest('[data-qty-max]');
      if (!wrapper) return;

      const maxQty = parseInt(wrapper.dataset.qtyMax, 10);
      const input = wrapper.querySelector('.js-qty__num');
      if (!input) return;

      if (parseInt(input.value, 10) >= maxQty) {
        event.preventDefault();
        event.stopImmediatePropagation();
        plusBtn.disabled = true;
      }
    },
    true
  );

  document.addEventListener('input', function (event) {
    if (!event.target.classList.contains('js-qty__num')) return;

    const wrapper = event.target.closest('[data-qty-max]');
    if (!wrapper) return;

    const maxQty = parseInt(wrapper.dataset.qtyMax, 10);
    const plusBtn = wrapper.querySelector('.js-qty__adjust--plus');
    if (plusBtn) plusBtn.disabled = parseInt(event.target.value, 10) >= maxQty;
  });
})();
